/**
 * Alternate Bills Hook
 * React hook for fetching alternate bills for a client organization
 * Uses the new Organization API
 */

import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/lib/api'

export interface AlternateBill {
  Id: number
  OrganizationClientRoleId: number
  OrganizationClientRoleAlternateBillStatusCode: string
  AlternateBillLegalName: string
  AlternateBillCode: string
}

interface UseAlternateBillsProps {
  organizationId?: number
}

interface UseAlternateBillsReturn {
  alternateBills: AlternateBill[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useAlternateBills({ organizationId }: UseAlternateBillsProps): UseAlternateBillsReturn {
  const { organizationApi } = useApi()
  const [alternateBills, setAlternateBills] = useState<AlternateBill[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAlternateBills = useCallback(async (orgId: number) => {
    if (!orgId) {
      setAlternateBills([])
      setLoading(false)
      setError(null)
      return
    }

    console.log(`Fetching alternate bills for organization ${orgId}...`)
    setLoading(true)
    setError(null)

    try {
      const response = await organizationApi.get<AlternateBill[]>(
        `Organization/${orgId}/latest-relevant-version/client-role/alternate-bills`
      )
      
      const bills = response || []
      setAlternateBills(bills)
      console.log(`Loaded ${bills.length} alternate bills for organization ${orgId}`)
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to load alternate bills for organization ${orgId}`
      setError(errorMessage)
      setAlternateBills([])
      console.error(`Failed to load alternate bills for organization ${orgId}:`, err)
    } finally {
      setLoading(false)
    }
  }, [organizationApi])

  // Fetch alternate bills when organizationId changes
  useEffect(() => {
    if (organizationId) {
      fetchAlternateBills(organizationId)
    }
  }, [organizationId])

  const refresh = useCallback(async () => {
    if (organizationId) {
      await fetchAlternateBills(organizationId)
    }
  }, [fetchAlternateBills, organizationId])

  return {
    alternateBills,
    loading,
    error,
    refresh
  }
} 